import { Box, createFilterOptions, ListSubheader } from '@mui/material';
import { useMemo } from 'react';
import { Control, FieldErrors, FieldValues } from 'react-hook-form';
import {
  DimensionClassOptionLabels,
  DimensionClassOptions,
  UnitOfMeasureDetails,
  UnitOfMeasureDetailsType,
} from 'types/unitsOfMeasure';

import { HookFormAutocomplete } from 'ui-component/HookFormComponents/HookFormAutoComplete';

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: UnitOfMeasureDetailsType) =>
    `${option.abbreviation} ${option.label}`,
});

type HookFormUnitOfMeasureAutocompletePropsRequired<T extends FieldValues> = {
  disableRequiredProps?: false;
  name: string;
  label: string;
  control: Control<T>;
  limitToDimensionClass?: DimensionClassOptions;
  errors: FieldErrors;
  isLoading?: boolean;
  handleChange?: (option: UnitOfMeasureDetailsType) => void;
};

type HookFormUnitOfMeasureAutocompletePropsDisableRequired<
  T extends FieldValues
> = {
  disableRequiredProps: true;
  limitToDimensionClass?: DimensionClassOptions;
};

type HookFormUnitOfMeasureAutocompleteProps<T extends FieldValues> =
  | HookFormUnitOfMeasureAutocompletePropsDisableRequired<T>
  | HookFormUnitOfMeasureAutocompletePropsRequired<T>;

export const HookFormUnitOfMeasureAutocomplete = <T extends FieldValues>(
  props: HookFormUnitOfMeasureAutocompleteProps<T>
) => {
  const { limitToDimensionClass, ...autocompletePassThroughProps } = props;
  const options = useMemo(() => {
    if (!limitToDimensionClass) {
      return Object.values(UnitOfMeasureDetails);
    }
    return Object.values(UnitOfMeasureDetails).filter(
      (option) => option.dimensionClass === limitToDimensionClass
    );
  }, [limitToDimensionClass]);
  return (
    <Box>
      <HookFormAutocomplete
        {...autocompletePassThroughProps}
        options={options}
        getOptionLabel={(option) => `${option.abbreviation}`}
        renderOption={(p, option) => (
          <li {...p}>
            {option.label} ({option.abbreviation})
          </li>
        )}
        groupBy={(option) => option.dimensionClass}
        renderGroup={(params) => (
          <li key={params.key}>
            <ListSubheader>
              {
                DimensionClassOptionLabels[
                  params.group as DimensionClassOptions
                ]
              }
            </ListSubheader>
            <ul>{params.children}</ul>
          </li>
        )}
        filterOptions={filterOptions}
        slotProps={{
          popper: {
            style: { width: 'fit-content' },
            placement: 'bottom-end',
          },
        }}
        valueFromOption={(option) => option.value}
        initialValueMatcher={(value) =>
          options.find((option) => option.value === value) || null
        }
        disableClearable
      />
    </Box>
  );
};
